package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.*
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.transparent
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*
import kotlin.time.Instant

@Composable
fun ExistingMemberDetailsScreen(
    clubId: String,
    membershipId: String,
    viewModel: ExistingMemberDetailsViewModel = koinViewModel(parameters = { parametersOf(clubId, membershipId) }),
    navigateTo: (ExistingMemberDetailsViewModel.NavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()
    val contentState = (screenState as? ScreenState.Content)?.data

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
        bottomBar = {
            if (contentState?.hasChanges == true) {
                UpdateMemberFooter(
                    isUpdating = contentState.isUpdating,
                    updateError = contentState.updateError,
                    onUpdateClick = viewModel::updateMember,
                )
            }
        },
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = viewModel::fetchMember,
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> ExistingMemberDetailsContent(
                    uiState = state.data,
                    onBackClick = viewModel::onBackClick,
                    onStatusSelected = viewModel::onStatusSelected,
                    onRoleSelected = viewModel::onRoleSelected,
                    onMembershipDataChanged = viewModel::onMembershipDataChanged,
                )
            }
        }
    }
}

@Composable
private fun ExistingMemberDetailsContent(
    uiState: ExistingMemberDetailsViewModel.UiState,
    onBackClick: () -> Unit,
    onStatusSelected: (MembershipStatus) -> Unit,
    onRoleSelected: (UserRole) -> Unit,
    onMembershipDataChanged: (String, String) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        ExistingMemberDetailsHeader(onBackClick = onBackClick)

        Spacer(modifier = Modifier.height(24.dp))

        MemberIdentityCard(member = uiState.member)

        Spacer(modifier = Modifier.height(16.dp))

        ApprovalCard(member = uiState.member)

        Spacer(modifier = Modifier.height(16.dp))

        MembershipSettingsCard(
            status = uiState.status,
            role = uiState.role,
            onStatusSelected = onStatusSelected,
            onRoleSelected = onRoleSelected,
        )

        Spacer(modifier = Modifier.height(16.dp))

        MembershipDataCard(
            fields = uiState.membershipDataFields,
            membershipData = uiState.membershipData,
            validationErrors = uiState.validationErrors,
            onMembershipDataChanged = onMembershipDataChanged,
        )

        Spacer(modifier = Modifier.height(if (uiState.hasChanges) 112.dp else 28.dp))
    }
}

@Composable
private fun ExistingMemberDetailsHeader(onBackClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TextButton(
            onClick = onBackClick,
            modifier = Modifier.size(40.dp),
            contentPadding = PaddingValues(0.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_arrow_back),
                contentDescription = stringResource(Res.string.back_icon_cd),
                modifier = Modifier.size(28.dp),
                tint = MaterialTheme.colorScheme.onBackground,
            )
        }

        Spacer(modifier = Modifier.width(20.dp))

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = stringResource(Res.string.member_details),
                color = MaterialTheme.colorScheme.onBackground,
                style = MaterialTheme.typography.titleLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = stringResource(Res.string.member_details_page_description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun MemberIdentityCard(member: Membership) {
    val displayName = member.displayName()

    SectionSurface {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MemberDetailsAvatar(displayName = displayName)

            Spacer(modifier = Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = displayName,
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Spacer(modifier = Modifier.height(6.dp))
                Text(
                    text = member.userId,
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Spacer(modifier = Modifier.height(10.dp))
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    MetadataChip(text = member.status.label())
                    MetadataChip(text = member.role.label())
                }
            }
        }
    }
}

@Composable
private fun MemberDetailsAvatar(displayName: String) {
    Box(
        modifier = Modifier
            .size(58.dp)
            .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.12f), CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = displayName.initials(),
            color = MaterialTheme.colorScheme.primary,
            style = MaterialTheme.typography.titleLarge,
            maxLines = 1,
        )
    }
}

@Composable
private fun MetadataChip(text: String) {
    Surface(
        shape = RoundedCornerShape(50),
        color = MaterialTheme.colorScheme.primary.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.primary.copy(alpha = 0.24f)),
    ) {
        Text(
            text = text,
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp),
            color = MaterialTheme.colorScheme.primary,
            style = MaterialTheme.typography.labelMedium,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
private fun ApprovalCard(member: Membership) {
    SectionSurface {
        SectionTitle(text = stringResource(Res.string.approval))

        Spacer(modifier = Modifier.height(14.dp))

        val approval = member.approvedBy
        if (approval == null) {
            Text(
                text = stringResource(Res.string.not_approved_yet),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
            )
        } else {
            ReadOnlyInfoRow(
                label = stringResource(Res.string.approved_by),
                value = approval.adminUserName.ifBlank { approval.adminUserId },
            )
            Spacer(modifier = Modifier.height(12.dp))
            ReadOnlyInfoRow(
                label = stringResource(Res.string.approved_at),
                value = approval.approvalDate.dateTimeLabel(),
            )
        }

        Spacer(modifier = Modifier.height(12.dp))

        ReadOnlyInfoRow(
            label = stringResource(Res.string.requested_at),
            value = member.requestedAt.dateTimeLabel(),
        )
    }
}

@Composable
private fun ReadOnlyInfoRow(
    label: String,
    value: String,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = label,
            color = MaterialTheme.colorScheme.outline,
            style = MaterialTheme.typography.labelLarge,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = value,
            color = MaterialTheme.colorScheme.onSurface,
            style = MaterialTheme.typography.titleMedium,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
private fun MembershipSettingsCard(
    status: MembershipStatus,
    role: UserRole,
    onStatusSelected: (MembershipStatus) -> Unit,
    onRoleSelected: (UserRole) -> Unit,
) {
    SectionSurface {
        SectionTitle(text = stringResource(Res.string.membership_settings))

        Spacer(modifier = Modifier.height(16.dp))

        EnumDropdownField(
            label = stringResource(Res.string.membership_status),
            value = status.label(),
            options = MembershipStatus.entries,
            optionLabel = { it.label() },
            onOptionSelected = onStatusSelected,
        )

        Spacer(modifier = Modifier.height(12.dp))

        EnumDropdownField(
            label = stringResource(Res.string.member_role),
            value = role.label(),
            options = UserRole.entries,
            optionLabel = { it.label() },
            onOptionSelected = onRoleSelected,
        )
    }
}

@Composable
private fun <T> EnumDropdownField(
    label: String,
    value: String,
    options: List<T>,
    optionLabel: (T) -> String,
    onOptionSelected: (T) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }

    Box(modifier = Modifier.fillMaxWidth()) {
        OutlinedTextField(
            value = value,
            onValueChange = { },
            readOnly = true,
            label = { Text(label) },
            textStyle = MaterialTheme.typography.titleMedium,
            modifier = Modifier.fillMaxWidth(),
        )
        Box(
            modifier = Modifier
                .matchParentSize()
                .clickable { expanded = true },
        )
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.fillMaxWidth(),
        ) {
            options.forEach { option ->
                DropdownMenuItem(
                    text = { Text(optionLabel(option)) },
                    onClick = {
                        onOptionSelected(option)
                        expanded = false
                    },
                )
            }
        }
    }
}

@Composable
private fun MembershipDataCard(
    fields: List<MembershipDataItem>,
    membershipData: Map<String, String>,
    validationErrors: Set<String>,
    onMembershipDataChanged: (String, String) -> Unit,
) {
    SectionSurface {
        SectionTitle(text = stringResource(Res.string.membership_data_title))

        Spacer(modifier = Modifier.height(16.dp))

        if (fields.isEmpty()) {
            Text(
                text = stringResource(Res.string.no_membership_data_required),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
            )
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                fields.forEachIndexed { index, item ->
                    ConfiguredMembershipField(
                        item = item,
                        index = index,
                        value = membershipData[item.label].orEmpty(),
                        isError = validationErrors.contains(item.label),
                        onValueChange = { value -> onMembershipDataChanged(item.label, value) },
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun ConfiguredMembershipField(
    item: MembershipDataItem,
    index: Int,
    value: String,
    isError: Boolean,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val label = if (item.required) "${item.label} *" else item.label

    when (item.type) {
        MembershipDataTypes.SHORT_TEXT,
        MembershipDataTypes.UNKNOWN -> {
            ConfiguredTextField(
                value = value,
                onValueChange = onValueChange,
                label = label,
                isError = isError,
                modifier = modifier,
            )
        }

        MembershipDataTypes.LONG_TEXT -> {
            ConfiguredTextField(
                value = value,
                onValueChange = onValueChange,
                label = label,
                isError = isError,
                minLines = 3,
                modifier = modifier,
            )
        }

        MembershipDataTypes.NUMBER -> {
            ConfiguredTextField(
                value = value,
                onValueChange = onValueChange,
                label = label,
                isError = isError,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                modifier = modifier,
            )
        }

        MembershipDataTypes.DATE -> {
            var isDialogOpen by remember(index) { mutableStateOf(false) }
            val datePickerState = rememberDatePickerState()

            LaunchedEffect(datePickerState.selectedDateMillis) {
                datePickerState.selectedDateMillis?.let {
                    onValueChange(formatMembershipDate(it))
                }
            }

            Box(modifier = modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value = value,
                    onValueChange = { },
                    readOnly = true,
                    label = { Text(label) },
                    placeholder = { Text(stringResource(Res.string.select_date)) },
                    isError = isError,
                    supportingText = requiredSupportingText(isError),
                    modifier = Modifier.fillMaxWidth(),
                )
                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .clickable { isDialogOpen = true },
                )
            }

            if (isDialogOpen) {
                DatePickerDialog(
                    onDismissRequest = { isDialogOpen = false },
                    confirmButton = {
                        TextButton(onClick = { isDialogOpen = false }) {
                            Text(stringResource(Res.string.ok))
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { isDialogOpen = false }) {
                            Text(stringResource(Res.string.cancel))
                        }
                    },
                ) {
                    DatePicker(state = datePickerState)
                }
            }
        }

        MembershipDataTypes.DROP_DOWN -> {
            ConfiguredDropdownField(
                value = value,
                label = label,
                options = item.options.orEmpty(),
                isError = isError,
                onValueChange = onValueChange,
                modifier = modifier,
            )
        }

        MembershipDataTypes.MULTI_SELECT -> {
            ConfiguredMultiSelectField(
                value = value,
                label = label,
                options = item.options.orEmpty(),
                isError = isError,
                onValueChange = onValueChange,
                modifier = modifier,
            )
        }

        MembershipDataTypes.BOOLEAN -> {
            ConfiguredBooleanField(
                value = value.toBooleanStrictOrNull() ?: false,
                label = label,
                isError = isError,
                onValueChange = { onValueChange(it.toString()) },
                modifier = modifier,
            )
        }
    }
}

@Composable
private fun ConfiguredTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    isError: Boolean,
    modifier: Modifier = Modifier,
    minLines: Int = 1,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label) },
        minLines = minLines,
        isError = isError,
        supportingText = requiredSupportingText(isError),
        keyboardOptions = keyboardOptions,
        textStyle = MaterialTheme.typography.titleMedium,
        modifier = modifier.fillMaxWidth(),
    )
}

@Composable
private fun ConfiguredDropdownField(
    value: String,
    label: String,
    options: List<String>,
    isError: Boolean,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (options.isEmpty()) {
        ConfiguredTextField(
            value = value,
            onValueChange = onValueChange,
            label = label,
            isError = isError,
            modifier = modifier,
        )
        return
    }

    var expanded by remember { mutableStateOf(false) }

    Box(modifier = modifier.fillMaxWidth()) {
        OutlinedTextField(
            value = value,
            onValueChange = { },
            readOnly = true,
            label = { Text(label) },
            isError = isError,
            supportingText = requiredSupportingText(isError),
            textStyle = MaterialTheme.typography.titleMedium,
            modifier = Modifier.fillMaxWidth(),
        )
        Box(
            modifier = Modifier
                .matchParentSize()
                .clickable { expanded = true },
        )
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.fillMaxWidth(),
        ) {
            options.forEach { option ->
                DropdownMenuItem(
                    text = { Text(option) },
                    onClick = {
                        onValueChange(option)
                        expanded = false
                    },
                )
            }
        }
    }
}

@Composable
private fun ConfiguredMultiSelectField(
    value: String,
    label: String,
    options: List<String>,
    isError: Boolean,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (options.isEmpty()) {
        ConfiguredTextField(
            value = value,
            onValueChange = onValueChange,
            label = label,
            isError = isError,
            modifier = modifier,
        )
        return
    }

    val selectedOptions = value.selectedOptions()

    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = if (isError) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface,
        )
        if (isError) {
            Text(
                text = stringResource(Res.string.field_required),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.error,
            )
        }

        options.forEach { option ->
            Row(
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Checkbox(
                    checked = selectedOptions.contains(option),
                    onCheckedChange = { checked ->
                        val updatedOptions = if (checked) {
                            selectedOptions + option
                        } else {
                            selectedOptions - option
                        }
                        onValueChange(updatedOptions.joinToString(", "))
                    },
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = option,
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleMedium,
                )
            }
        }
    }
}

@Composable
private fun ConfiguredBooleanField(
    value: Boolean,
    label: String,
    isError: Boolean,
    onValueChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = label,
                modifier = Modifier.weight(1f),
                color = if (isError) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface,
                style = MaterialTheme.typography.titleMedium,
            )
            Switch(
                checked = value,
                onCheckedChange = onValueChange,
            )
        }
        if (isError) {
            Text(
                text = stringResource(Res.string.field_required),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.error,
            )
        }
    }
}

@Composable
private fun requiredSupportingText(isError: Boolean): (@Composable () -> Unit)? {
    return if (isError) {
        {
            Text(stringResource(Res.string.field_required))
        }
    } else {
        null
    }
}

@Composable
private fun SectionSurface(content: @Composable ColumnScope.() -> Unit) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.6f)),
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            content = content,
        )
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(
        text = text,
        color = MaterialTheme.colorScheme.onSurface,
        style = MaterialTheme.typography.titleLarge,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
    )
}

@Composable
private fun UpdateMemberFooter(
    isUpdating: Boolean,
    updateError: Boolean,
    onUpdateClick: () -> Unit,
) {
    Surface(
        tonalElevation = 8.dp,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.4f)),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
        ) {
            Text(
                text = stringResource(
                    if (updateError) {
                        Res.string.membership_update_error
                    } else {
                        Res.string.unsaved_changes
                    }
                ),
                color = if (updateError) {
                    MaterialTheme.colorScheme.error
                } else {
                    MaterialTheme.colorScheme.outline
                },
                style = MaterialTheme.typography.labelLarge,
            )

            Spacer(modifier = Modifier.height(8.dp))

            Button(
                onClick = onUpdateClick,
                enabled = !isUpdating,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = MaterialTheme.colorScheme.onPrimary,
                ),
            ) {
                if (isUpdating) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(18.dp),
                        strokeWidth = 2.dp,
                        color = MaterialTheme.colorScheme.onPrimary,
                    )
                    Spacer(modifier = Modifier.width(10.dp))
                }
                Text(text = stringResource(Res.string.update))
            }
        }
    }
}

private fun Membership.displayName(): String {
    return userDisplayName.ifBlank { userId }
}

private fun String.initials(): String {
    val parts = trim().split(" ").filter { it.isNotBlank() }
    return parts.take(2).joinToString("") { it.first().uppercase() }.ifBlank { "?" }
}

private fun MembershipStatus?.label(): String {
    return when (this) {
        MembershipStatus.PENDING -> "Pending"
        MembershipStatus.APPROVED -> "Approved"
        MembershipStatus.BLOCKED -> "Blocked"
        MembershipStatus.EXPIRED -> "Expired"
        MembershipStatus.UNKNOWN, null -> "Unknown"
    }
}

private fun UserRole.label(): String {
    return when (this) {
        UserRole.MEMBER -> "Member"
        UserRole.ADMIN -> "Admin"
        UserRole.SUPER_ADMIN -> "Super admin"
    }
}

private fun String?.dateTimeLabel(): String {
    val value = this?.trim().orEmpty()
    if (value.isBlank()) return "-"

    return value.substringBefore(".").replace("T", " ")
}

private fun formatMembershipDate(millis: Long): String {
    val instant = Instant.fromEpochMilliseconds(millis)
    val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    val day = dateTime.day.toString().padStart(2, '0')
    val month = dateTime.month.number.toString().padStart(2, '0')
    val year = dateTime.year

    return "$day-$month-$year"
}

private fun String.selectedOptions(): Set<String> {
    return split(",")
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .toSet()
}

@Preview(name = "Existing Member Details")
@Composable
private fun ExistingMemberDetailsPreview() {
    RainbowClubTheme {
        Surface {
            ExistingMemberDetailsContent(
                uiState = ExistingMemberDetailsViewModel.UiState(
                    member = sampleMemberDetails(),
                    status = MembershipStatus.APPROVED,
                    role = UserRole.ADMIN,
                    membershipDataFields = sampleMembershipDataFields(),
                    membershipData = sampleMemberDetails().membershipData + ("T-shirt size" to "Large"),
                ),
                onBackClick = {},
                onStatusSelected = {},
                onRoleSelected = {},
                onMembershipDataChanged = { _, _ -> },
            )
        }
    }
}

private fun sampleMembershipDataFields(): List<MembershipDataItem> {
    return listOf(
        MembershipDataItem(
            label = "Phone number",
            type = MembershipDataTypes.SHORT_TEXT,
            required = true,
        ),
        MembershipDataItem(
            label = "Emergency contact",
            type = MembershipDataTypes.LONG_TEXT,
            required = false,
        ),
        MembershipDataItem(
            label = "T-shirt size",
            type = MembershipDataTypes.DROP_DOWN,
            options = listOf("Small", "Medium", "Large"),
            required = true,
        ),
        MembershipDataItem(
            label = "Birth date",
            type = MembershipDataTypes.DATE,
            required = false,
        ),
        MembershipDataItem(
            label = "Photo consent",
            type = MembershipDataTypes.BOOLEAN,
            required = false,
        ),
    )
}

private fun sampleMemberDetails(): Membership {
    return Membership(
        userDisplayName = "Alex Doe",
        userId = "alex@example.com",
        clubName = "Rainbow Club",
        clubId = "club-1",
        membershipId = "membership-1",
        role = UserRole.MEMBER,
        status = MembershipStatus.APPROVED,
        membershipData = mapOf(
            "Phone number" to "+46 70 123 45 67",
            "Emergency contact" to "Mina Patel",
            "T-shirt size" to "Medium",
            "Birth date" to "2001-04-22",
            "Photo consent" to "true",
        ),
        requestedAt = "2026-06-17T10:15:30Z",
        approvedBy = MembershipApproval(
            adminUserId = "nora-admin",
            adminUserName = "Nora Admin",
            approvalDate = "2026-06-17T12:42:00Z",
        ),
    )
}
